/**
 * Council Trail v0 — persona color resolution.
 *
 * Resolution order (spec §6.3):
 *   1. persona.accent_color (palette index 1-10) — wins if set + in range
 *   2. session-level color_map (cross-host stability) — wins if entry present
 *   3. name-hash fallback — deterministic by persona.id
 *
 * Each PersonaColor exposes paired Tailwind shades (light/dark variants)
 * so dark-mode parity ships from day one (per feedback_dark_mode_parity_first_class).
 */

export interface PersonaColor {
  hue: string;
  /** Tailwind class name for light-mode static contexts (e.g. 'indigo-500'). */
  light: string;
  /** Tailwind class name for dark-mode static contexts. */
  dark: string;
  /** Raw hex string for inline-style contexts where JIT purges dynamic classes. */
  hexLight: string;
  hexDark: string;
  paletteIndex: number;
}

export const PALETTE: ReadonlyArray<Omit<PersonaColor, 'paletteIndex'>> = [
  { hue: 'indigo',  light: 'indigo-500',  dark: 'indigo-400',  hexLight: '#6366f1', hexDark: '#818cf8' },
  { hue: 'cyan',    light: 'cyan-500',    dark: 'cyan-400',    hexLight: '#06b6d4', hexDark: '#22d3ee' },
  { hue: 'violet',  light: 'violet-500',  dark: 'violet-400',  hexLight: '#8b5cf6', hexDark: '#a78bfa' },
  { hue: 'rose',    light: 'rose-500',    dark: 'rose-400',    hexLight: '#f43f5e', hexDark: '#fb7185' },
  { hue: 'lime',    light: 'lime-500',    dark: 'lime-400',    hexLight: '#84cc16', hexDark: '#a3e635' },
  { hue: 'fuchsia', light: 'fuchsia-500', dark: 'fuchsia-400', hexLight: '#d946ef', hexDark: '#e879f9' },
  { hue: 'sky',     light: 'sky-500',     dark: 'sky-400',     hexLight: '#0ea5e9', hexDark: '#38bdf8' },
  { hue: 'orange',  light: 'orange-500',  dark: 'orange-400',  hexLight: '#f97316', hexDark: '#fb923c' },
  { hue: 'teal',    light: 'teal-500',    dark: 'teal-400',    hexLight: '#14b8a6', hexDark: '#2dd4bf' },
  { hue: 'pink',    light: 'pink-500',    dark: 'pink-400',    hexLight: '#ec4899', hexDark: '#f472b6' },
];

function hashStringToPaletteIndex(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE.length;
}

export function getPersonaColor(
  persona: { id: string; accent_color?: number | null; name: string },
  sessionColorMap?: Record<string, number>,
): PersonaColor {
  if (persona.accent_color != null
      && persona.accent_color >= 1
      && persona.accent_color <= PALETTE.length) {
    const idx = persona.accent_color - 1;
    return { ...PALETTE[idx], paletteIndex: persona.accent_color };
  }

  if (sessionColorMap && sessionColorMap[persona.id]) {
    const mapped = sessionColorMap[persona.id];
    const idx = mapped - 1;
    if (idx >= 0 && idx < PALETTE.length) {
      return { ...PALETTE[idx], paletteIndex: mapped };
    }
  }

  const idx = hashStringToPaletteIndex(persona.id);
  return { ...PALETTE[idx], paletteIndex: idx + 1 };
}
